import { useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CollectionModel } from '../../data-layer/collections/types';
import { CollectionStatusDrawer } from '../../ui/collection/CollectionStatusDrawer';
import { Typography } from '../../ui/typography/Typography';
import { CollectionStatus } from '../../ui/users/types';
import { ymTrackServiceRulesClick, ymTrackSupportClick } from '../../utils/metrika';
import { useBackButton } from '../../utils/useBackButton';
import { useTranslation } from '../../utils/useTranslation';
import { useOpenDialog } from '../ModalRoute';
import { DEFAULT_CLOSE_ANIMATION_DELAY, DIALOGS } from '../constants';
import { useCurrentUser } from '../users/useCurrentUser';

const statusMap = {
    [CollectionStatus.MODERATION]: {
        title: 'Коллекция на модерации',
    },
    [CollectionStatus.PUBLISHED]: {
        title: 'Коллекция опубликована',
    },
    [CollectionStatus.DECLINED]: {
        title: 'Коллекция отклонена',
    },
    [CollectionStatus.DRAFT]: {
        title: 'Черновик',
    },
};

const getDescriptionSlot = (status: CollectionStatus, onRulesClick: VoidFunction) => {
    const t = useTranslation();
    if (status === CollectionStatus.MODERATION) {
        return (
            <Typography variant="body">
                {t('Поделиться коллекцией можно только после прохождения модерации')}
            </Typography>
        );
    }
    if (status === CollectionStatus.DECLINED) {
        return (
            <>
                <Typography variant="body">
                    {t('Коллекция отклонена по ')}
                    <Typography
                        variant="body"
                        display="inline"
                        as={Link}
                        to="https://yandex.ru/support2/rythm/ru/buisness/moderation#trebovaniya-k-tekstam"
                        color="link"
                        textDecoration="none"
                        onClick={onRulesClick}
                    >
                        {t('правилам сервиса')}
                    </Typography>
                    {t('. Поправьте название, и мы снова ее проверим.')}
                </Typography>
                <Typography variant="body" display="inline" pt={2}>
                    {t('Если у вас остались вопросы, обращайтесь в поддержку, мы всегда на связи.')}
                </Typography>
            </>
        );
    }
};

interface CollectionStatusDialogState {
    collection: CollectionModel;
}

export const CollectionStatusDialog = () => {
    const state = useLocation().state as CollectionStatusDialogState;
    const { collection } = state;
    const onNavigateBack = useBackButton();
    const openDialog = useOpenDialog();
    const handleEditName = useCallback(() => {
        setTimeout(() => {
            openDialog(DIALOGS.EDIT_COLLECTION, { collectionId: collection.id });
        }, DEFAULT_CLOSE_ANIMATION_DELAY);
    }, [openDialog, collection.id]);
    const closeDialog = useCallback(() => {
        setTimeout(onNavigateBack, DEFAULT_CLOSE_ANIMATION_DELAY);
    }, [onNavigateBack]);
    const currentUser = useCurrentUser();
    const onRulesClick = useCallback(() => {
        ymTrackServiceRulesClick({
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
            page: 'Коллекция',
            own: collection.modifiable,
            isPublic: collection.isPublic,
        });
    }, [currentUser]);
    const onSupportClick = useCallback(() => {
        ymTrackSupportClick({
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
            page: 'Коллекция',
            own: collection.modifiable,
            isPublic: collection.isPublic,
        });
    }, [currentUser, collection.modifiable]);

    return (
        <CollectionStatusDrawer
            handleClose={closeDialog}
            handleEditName={handleEditName}
            status={collection.status}
            title={statusMap[collection.status].title}
            descriptionSlot={getDescriptionSlot(collection.status, onRulesClick)}
            currentUser={currentUser}
            onSupportClick={onSupportClick}
        />
    );
};
